import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/Button'
import { Dialog } from '@/components/ui/Dialog'
import { MorphSelect } from '@/components/ui/MorphSelect'
import { morphToast } from '@/components/ui/Toast'
import { CloseIcon } from '@/components/ui/icons'
import { useModalStore } from '@/hooks/useModalStore'
import { useTableEditorModal } from '@/hooks/useTableEditorModal'
import { useColumnConfirmDelete } from '@/hooks/useColumnConfirmDelete'
import { metaToColumn } from '@/services/metadata/adapters'
import { markTouchedColumns } from '@/services/realtime/touched'
import { useDiagramStore, useSelectionStore, useViewportStore } from '@/stores'
import { useDraftStore } from '@/stores/draftStore'
import type { Column } from '@/types'
import { isForeignKey, isPrimaryKey } from '@/utils/columnKeys'
import { genId } from '@/utils/id'
import { calcTableHeight, TW } from '@/utils/geometry'
import { buildFullTableName, normCol } from '@/utils/normalizers'
import { isValidSqlIdentifier, sanitizeSqlIdentifierInput } from '@/utils/sqlIdentifiers'
import { normalizeColumnType } from './ColumnTypeInput'
import { ColumnRow } from './ColumnRow'
import type { ColumnImpact, DraftColumn } from './tableEditorTypes'

function buildDraftColumn(column?: Partial<Column>): DraftColumn {
  const normalized = normCol(column ?? {})
  return {
    ...normalized,
    rowId: genId('col-row'),
    sourceName: normalized.name,
    descOpen: false,
  }
}

function buildDefaultColumns(defaults: { prefix: Column[]; suffix: Column[] }): DraftColumn[] {
  return [
    ...defaults.prefix.map((column) => buildDraftColumn(column)),
    buildDraftColumn({ blank: true }),
    ...defaults.suffix.map((column) => buildDraftColumn(column)),
  ]
}

function serializeDraft(input: {
  name: string
  schema: string
  comment: string
  groupId: string
  columns: DraftColumn[]
}) {
  return JSON.stringify({
    name: input.name.trim(),
    schema: input.schema.trim(),
    comment: input.comment.trim(),
    groupId: input.groupId,
    columns: input.columns.map((column) => ({
      key: column.key,
      pk: !!column.pk,
      fk: !!column.fk,
      uq: !!column.uq,
      name: column.name.trim(),
      type: normalizeColumnType(column.type),
      notNull: column.notNull,
      blank: column.blank,
      desc: column.desc.trim(),
      defaultValue: column.defaultValue?.trim() ?? '',
      checkExpression: column.checkExpression?.trim() ?? '',
    })),
  })
}

interface DraftHistorySnapshot {
  name: string
  schema: string
  comment: string
  groupId: string
  columns: DraftColumn[]
}

function cloneDraftHistorySnapshot(snapshot: DraftHistorySnapshot): DraftHistorySnapshot {
  return {
    ...snapshot,
    columns: snapshot.columns.map((column) => ({ ...column })),
  }
}

/**
 * Reanexa as colunas que outro usuário criou nesta tabela depois que o modal
 * abriu. O buffer local não as conhece, e mandá-lo como está faria o diff
 * emitir `column.remove` para elas. Só some o que foi removido explicitamente
 * aqui (`removedColumnIds`) — ausência do buffer, por si, não é deleção.
 *
 * A fonte é o espelho do servidor (`draftStore.metadata`), não o `diagramStore`:
 * quando há edição local pendente, o canvas não recebe as ops remotas.
 */
function mergeConcurrentColumns(
  localColumns: Column[],
  tableId: string | undefined,
  removedColumnIds: ReadonlySet<string>,
): Column[] {
  if (!tableId) return localColumns

  const mirrorTable = useDraftStore.getState().metadata?.tables[tableId]
  if (!mirrorTable) return localColumns

  const localIds = new Set(localColumns.map((column) => column.id))
  const concurrent = mirrorTable.colOrder
    .filter((columnId) => !localIds.has(columnId) && !removedColumnIds.has(columnId))
    .map((columnId) => mirrorTable.cols[columnId])
    .filter(Boolean)
    .map((column) => normCol(metaToColumn(column)))

  return concurrent.length > 0 ? [...localColumns, ...concurrent] : localColumns
}

function resolveNewTablePosition(columnCount: number) {
  const { sc, tx, ty, containerWidth, containerHeight } = useViewportStore.getState()
  const centerX = containerWidth / (2 * sc) - tx
  const centerY = containerHeight / (2 * sc) - ty
  return {
    x: Math.round(centerX - TW / 2),
    y: Math.round(centerY - calcTableHeight(columnCount) / 2),
  }
}

export function TableEditorModal() {
  const inlineAddColumnResumeDelayMs = 1280

  const { t } = useTranslation()
  const open = useTableEditorModal((state) => state.open)
  const tableId = useTableEditorModal((state) => state.tableId)
  const createPosition = useTableEditorModal((state) => state.createPosition)
  const suggestedName = useTableEditorModal((state) => state.suggestedName)
  const closeModal = useTableEditorModal((state) => state.close)
  const openDefaults = useModalStore((state) => state.openDefaults)

  const tables = useDiagramStore((state) => state.tables)
  const rels = useDiagramStore((state) => state.rels)
  const groups = useDiagramStore((state) => state.groups)
  const dialect = useDiagramStore((state) => state.dialect)
  const saveTable = useDiagramStore((state) => state.saveTable)
  const defaults = useDiagramStore((state) => state.defaults)
  const currentTable = useMemo(
    () => tables.find((entry) => entry.id === tableId) ?? null,
    [tableId, tables],
  )
  const hasDefaultsTemplate = useMemo(
    () => defaults.prefix.length > 0 || defaults.suffix.length > 0,
    [defaults],
  )
  const remoteTableEdit = useDraftStore((state) =>
    tableId ? state.remoteTableEdits[tableId] ?? null : null,
  )

  const [name, setName] = useState('')
  const [schema, setSchema] = useState('')
  const [comment, setComment] = useState('')
  const [groupId, setGroupId] = useState('')
  const [columns, setColumns] = useState<DraftColumn[]>([])
  const [errorMessage, setErrorMessage] = useState('')
  const [showDiscardDialog, setShowDiscardDialog] = useState(false)
  const [hoveredInsertIndex, setHoveredInsertIndex] = useState<number | null>(null)
  const [isSortingColumns, setIsSortingColumns] = useState(false)
  const [canScrollColumnsUp, setCanScrollColumnsUp] = useState(false)
  const [canScrollColumnsDown, setCanScrollColumnsDown] = useState(false)
  const [enteringColumnRowId, setEnteringColumnRowId] = useState<string | null>(null)
  const [autoFocusColumnRowId, setAutoFocusColumnRowId] = useState<string | null>(null)
  const [remoteNoticeBaseRev, setRemoteNoticeBaseRev] = useState<number | null>(null)
  const columnsScrollRef = useRef<HTMLDivElement | null>(null)
  const tableNameInputRef = useRef<HTMLInputElement | null>(null)
  const initialSnapshotRef = useRef('')
  const seededColumnIdsRef = useRef<Set<string>>(new Set())
  const removedColumnIdsRef = useRef<Set<string>>(new Set())
  const draftUndoRef = useRef<DraftHistorySnapshot[]>([])
  const draftRedoRef = useRef<DraftHistorySnapshot[]>([])
  const isApplyingDraftHistoryRef = useRef(false)
  const isDraftHistoryReadyRef = useRef(false)
  const lastDraftSnapshotRef = useRef('')
  const entryAnimationTimeoutRef = useRef<number | null>(null)

  const { isConfirming, requestDelete, cancelConfirmation, confirmingId } = useColumnConfirmDelete()

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
  )

  /**
   * O template de defaults só participa da semeadura ao CRIAR uma tabela. Ao
   * editar, a chave fica nula para que uma alteração de defaults (inclusive
   * vinda de outro usuário) não re-semeie o formulário em cima da digitação.
   */
  const seedDefaultsKey = useMemo(
    () => (tableId ? null : JSON.stringify(defaults)),
    [defaults, tableId],
  )

  useEffect(() => {
    if (!open) {
      setRemoteNoticeBaseRev(null)
      return
    }

    /*
     * A semeadura lê o store de forma pontual em vez de depender do objeto
     * reativo: `loadDiagram` (disparado por qualquer op remota, mesmo de outra
     * tabela) recria o array de tabelas, e depender dele reescrevia o buffer
     * local por cima do que o usuário está digitando aqui.
     */
    const diagram = useDiagramStore.getState()
    const seedTable = tableId ? diagram.tables.find((entry) => entry.id === tableId) ?? null : null

    let nextName = suggestedName
    let nextSchema = ''
    let nextComment = ''
    let nextGroupId = ''
    let nextColumns = buildDefaultColumns(diagram.defaults)

    if (seedTable) {
      nextName = seedTable.name
      nextSchema = seedTable.schema
      nextComment = seedTable.comment
      nextGroupId = seedTable.g
      nextColumns = seedTable.cols.map((column) => buildDraftColumn(column))
    }

    const baselineSnapshot: DraftHistorySnapshot = {
      name: nextName,
      schema: nextSchema,
      comment: nextComment,
      groupId: nextGroupId,
      columns: nextColumns.map((column) => ({ ...column })),
    }

    initialSnapshotRef.current = serializeDraft(baselineSnapshot)
    seededColumnIdsRef.current = new Set(
      (seedTable?.cols ?? []).map((column) => column.id).filter(Boolean),
    )
    removedColumnIdsRef.current = new Set()
    setRemoteNoticeBaseRev(useDraftStore.getState().rev)

    draftUndoRef.current = [cloneDraftHistorySnapshot(baselineSnapshot)]
    draftRedoRef.current = []
    isApplyingDraftHistoryRef.current = true
    isDraftHistoryReadyRef.current = true
    lastDraftSnapshotRef.current = serializeDraft(baselineSnapshot)

    setName(nextName)
    setSchema(nextSchema)
    setComment(nextComment)
    setGroupId(nextGroupId)
    setColumns(nextColumns)
    setErrorMessage('')
    cancelConfirmation()
    setShowDiscardDialog(false)
    setEnteringColumnRowId(null)
    setAutoFocusColumnRowId(null)

    requestAnimationFrame(() => {
      tableNameInputRef.current?.focus()
      tableNameInputRef.current?.select()
    })
  }, [open, tableId, seedDefaultsKey, suggestedName, cancelConfirmation])

  useEffect(() => {
    return () => {
      if (entryAnimationTimeoutRef.current !== null) {
        window.clearTimeout(entryAnimationTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (isSortingColumns) {
      setHoveredInsertIndex(null)
    }
  }, [isSortingColumns])

  const currentSnapshot = useMemo(
    () => serializeDraft({ name, schema, comment, groupId, columns }),
    [name, schema, comment, groupId, columns],
  )

  const buildDraftSnapshot = useCallback(
    (): DraftHistorySnapshot => ({
      name,
      schema,
      comment,
      groupId,
      columns: columns.map((column) => ({ ...column })),
    }),
    [columns, comment, groupId, name, schema],
  )

  const applyDraftSnapshot = useCallback((snapshot: DraftHistorySnapshot) => {
    isApplyingDraftHistoryRef.current = true
    setName(snapshot.name)
    setSchema(snapshot.schema)
    setComment(snapshot.comment)
    setGroupId(snapshot.groupId)
    setColumns(snapshot.columns.map((column) => ({ ...column })))
  }, [])

  const isDirty = currentSnapshot !== initialSnapshotRef.current

  const showRemoteEditNotice =
    remoteNoticeBaseRev !== null &&
    remoteTableEdit !== null &&
    remoteTableEdit.rev > remoteNoticeBaseRev

  const sortableIds = useMemo(() => columns.map((col) => col.rowId), [columns])

  useEffect(() => {
    if (!open) {
      draftUndoRef.current = []
      draftRedoRef.current = []
      isApplyingDraftHistoryRef.current = false
      isDraftHistoryReadyRef.current = false
      lastDraftSnapshotRef.current = ''
      return
    }

    const snapshot = buildDraftSnapshot()

    if (!isDraftHistoryReadyRef.current) {
      draftUndoRef.current = [cloneDraftHistorySnapshot(snapshot)]
      draftRedoRef.current = []
      isDraftHistoryReadyRef.current = true
      lastDraftSnapshotRef.current = currentSnapshot
      return
    }

    if (isApplyingDraftHistoryRef.current) {
      isApplyingDraftHistoryRef.current = false
      lastDraftSnapshotRef.current = currentSnapshot
      return
    }

    if (currentSnapshot === lastDraftSnapshotRef.current) return

    draftUndoRef.current = [
      ...draftUndoRef.current,
      cloneDraftHistorySnapshot(snapshot),
    ].slice(-100)
    draftRedoRef.current = []
    lastDraftSnapshotRef.current = currentSnapshot
  }, [buildDraftSnapshot, currentSnapshot, open])

  const normalizeColumnsForSave = useCallback(() => {
    const rowsWithContent = columns.filter((column) =>
      [
        column.name.trim(),
        column.type.trim(),
        column.desc.trim(),
        column.defaultValue?.trim(),
        column.checkExpression?.trim(),
      ].some(Boolean) ||
      column.pk ||
      column.fk ||
      column.uq ||
      column.notNull ||
      !column.blank,
    )

    const unnamed = rowsWithContent.find((column) => !column.name.trim())
    if (unnamed) {
      return { error: t('tableModal.columnNameRequired') }
    }

    const invalidColumn = rowsWithContent.find((column) => !isValidSqlIdentifier(column.name.trim()))
    if (invalidColumn) {
      return { error: t('tableModal.invalidColumnName', { name: invalidColumn.name.trim() || '—' }) }
    }

    const missingTypeColumn = rowsWithContent.find((column) => !column.type.trim())
    if (missingTypeColumn) {
      return {
        error: t('tableModal.columnTypeRequired', {
          name: missingTypeColumn.name.trim() || '—',
        }),
      }
    }

    const normalized = rowsWithContent.map((column) =>
      normCol({
        // Sem repassar o id, `normCol` gera um UUID novo e a coluna perde a
        // identidade a cada save — o diff vira remove+add de tudo e qualquer
        // merge por UUID deixa de funcionar.
        id: column.id,
        key: column.key,
        pk: column.pk,
        fk: column.fk,
        uq: column.pk ? true : column.uq,
        name: column.name.trim(),
        type: normalizeColumnType(column.type),
        notNull: column.notNull,
        blank: column.blank,
        desc: column.desc.trim(),
        defaultValue: column.defaultValue?.trim() ?? '',
        checkExpression: column.checkExpression?.trim() ?? '',
      }),
    )

    const pkCount = normalized.filter((column) => isPrimaryKey(column)).length
    if (normalized.length === 0) {
      return { error: t('tableModal.emptyColumns', 'A tabela precisa ter pelo menos uma coluna.') }
    }
    if (pkCount > 1) {
      return { error: t('tableModal.singlePkError') }
    }

    return { columns: normalized }
  }, [columns, t])

  const handleSave = useCallback(() => {
    setErrorMessage('')

    const trimmedName = name.trim()
    const trimmedSchema = schema.trim()
    const trimmedComment = comment.trim()

    if (!trimmedName) {
      setErrorMessage(t('prompts.tableNameRequired'))
      return
    }

    if (!isValidSqlIdentifier(trimmedName)) {
      setErrorMessage(t('tableModal.invalidTableName'))
      return
    }

    if (trimmedSchema && !isValidSqlIdentifier(trimmedSchema)) {
      setErrorMessage(t('tableModal.invalidSchemaName'))
      return
    }

    const duplicate = tables.find((table) =>
      table.id !== currentTable?.id &&
      table.name === trimmedName &&
      table.schema === trimmedSchema,
    )

    if (duplicate) {
      setErrorMessage(t('tableModal.duplicateSchemaName', { name: buildFullTableName(trimmedSchema, trimmedName) }))
      return
    }

    const normalizedResult = normalizeColumnsForSave()
    if (!normalizedResult.columns) {
      setErrorMessage(normalizedResult.error || t('common.notice'))
      return
    }

    const renamedFkColumns = columns
      .filter((column) =>
        isForeignKey(column) &&
        column.sourceName &&
        column.sourceName !== column.name.trim(),
      )
      .map((column) => ({
        previousName: column.sourceName,
        nextName: column.name.trim(),
      }))

    const mergedColumns = mergeConcurrentColumns(
      normalizedResult.columns,
      currentTable?.id,
      removedColumnIdsRef.current,
    )

    const position = currentTable
      ? { x: currentTable.x, y: currentTable.y }
      : createPosition ?? resolveNewTablePosition(mergedColumns.length)

    if (currentTable) {
      markTouchedColumns(currentTable.id, [
        ...mergedColumns.map((column) => column.id),
        ...removedColumnIdsRef.current,
      ])
    }

    const saved = saveTable({
      originalId: currentTable?.id,
      renamedFkColumns,
      table: {
        id: buildFullTableName(trimmedSchema, trimmedName),
        name: trimmedName,
        schema: trimmedSchema,
        fullName: buildFullTableName(trimmedSchema, trimmedName),
        comment: trimmedComment,
        g: groupId,
        x: position.x,
        y: position.y,
        cols: mergedColumns,
        indexes: currentTable?.indexes ?? [],
      },
    })

    if (!saved) {
      setErrorMessage(t('tableModal.saveFailed'))
      return
    }

    useSelectionStore.getState().selectTable(saved.id)
    closeModal()
    morphToast.success(
      t(currentTable ? 'prompts.tableUpdated' : 'prompts.tableCreated', { name: saved.fullName }),
    )
  }, [
    closeModal,
    columns,
    createPosition,
    currentTable,
    groupId,
    name,
    saveTable,
    schema,
    tables,
    comment,
    normalizeColumnsForSave,
    t,
  ])

  const requestClose = useCallback(() => {
    cancelConfirmation()
    if (isDirty) {
      setShowDiscardDialog(true)
      return
    }
    closeModal()
  }, [cancelConfirmation, closeModal, isDirty])

  const handleUndoDraft = useCallback(() => {
    if (draftUndoRef.current.length <= 1) return false

    const current = draftUndoRef.current[draftUndoRef.current.length - 1]
    const previous = draftUndoRef.current[draftUndoRef.current.length - 2]
    if (!current || !previous) return false

    draftRedoRef.current = [
      ...draftRedoRef.current,
      cloneDraftHistorySnapshot(current),
    ].slice(-100)
    draftUndoRef.current = draftUndoRef.current.slice(0, -1)
    applyDraftSnapshot(cloneDraftHistorySnapshot(previous))
    return true
  }, [applyDraftSnapshot])

  const handleRedoDraft = useCallback(() => {
    const next = draftRedoRef.current[draftRedoRef.current.length - 1]
    if (!next) return false

    draftRedoRef.current = draftRedoRef.current.slice(0, -1)
    draftUndoRef.current = [
      ...draftUndoRef.current,
      cloneDraftHistorySnapshot(next),
    ].slice(-100)
    applyDraftSnapshot(cloneDraftHistorySnapshot(next))
    return true
  }, [applyDraftSnapshot])

  useEffect(() => {
    if (!open) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z' && !event.shiftKey) {
        event.preventDefault()
        handleUndoDraft()
        return
      }

      if ((event.ctrlKey || event.metaKey) && (event.key.toLowerCase() === 'y' || (event.shiftKey && event.key.toLowerCase() === 'z'))) {
        event.preventDefault()
        handleRedoDraft()
        return
      }

      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
        event.preventDefault()
        handleSave()
        return
      }

      if (event.key !== 'Escape') return

      if (confirmingId) {
        event.preventDefault()
        cancelConfirmation()
        return
      }

      event.preventDefault()
      requestClose()
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [open, confirmingId, cancelConfirmation, requestClose, handleRedoDraft, handleSave, handleUndoDraft])

  const updateScrollHints = useCallback(() => {
    const container = columnsScrollRef.current
    if (!container) {
      setCanScrollColumnsUp(false)
      setCanScrollColumnsDown(false)
      return
    }

    const { scrollTop, clientHeight, scrollHeight } = container
    setCanScrollColumnsUp(scrollTop > 8)
    setCanScrollColumnsDown(scrollTop + clientHeight < scrollHeight - 8)
  }, [])

  useEffect(() => {
    if (!open) return

    const container = columnsScrollRef.current
    if (!container) return

    const sync = () => updateScrollHints()
    sync()
    container.addEventListener('scroll', sync)
    window.addEventListener('resize', sync)
    const frameId = window.requestAnimationFrame(sync)

    return () => {
      window.cancelAnimationFrame(frameId)
      container.removeEventListener('scroll', sync)
      window.removeEventListener('resize', sync)
    }
  }, [open, columns, updateScrollHints, hoveredInsertIndex, isSortingColumns])

  const groupOptions = useMemo(
    () => [
      {
        value: '',
        label: t('sidebar.noGroup'),
        color: '#94A3B8',
      },
      ...Object.values(groups).map((group) => ({
        value: group.id,
        label: group.name,
        color: group.color,
      })),
    ],
    [groups, t],
  )

  const impactByRowId = useMemo(() => {
    const relationColumns = new Set(
      currentTable
        ? rels
            .filter((relation) => relation.from === currentTable.id)
            .map((relation) => relation.col)
        : [],
    )

    const indexColumns = new Set(
      currentTable
        ? (currentTable.indexes ?? []).flatMap((index) => index.cols)
        : [],
    )

    return columns.reduce<Record<string, ColumnImpact>>((acc, column) => {
      acc[column.rowId] = {
        hasForeignKeyRelation: relationColumns.has(column.sourceName || column.name),
        hasIndexImpact: indexColumns.has(column.sourceName || column.name),
      }
      return acc
    }, {})
  }, [columns, currentTable, rels])

  if (!open) return null

  const addColumn = (index?: number) => {
    const next = buildDraftColumn({ blank: true })
    if (entryAnimationTimeoutRef.current !== null) {
      window.clearTimeout(entryAnimationTimeoutRef.current)
    }

    setEnteringColumnRowId(next.rowId)
    setAutoFocusColumnRowId(next.rowId)
    setColumns((current) => {
      if (index === undefined || index < 0 || index > current.length) {
        return [...current, next]
      }
      return [...current.slice(0, index), next, ...current.slice(index)]
    })

    window.requestAnimationFrame(() => {
      const rowInput = document.querySelector<HTMLInputElement>(
        `[data-column-row-id="${next.rowId}"] input`,
      )
      rowInput?.focus()
      rowInput?.select()
      rowInput?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    })

    entryAnimationTimeoutRef.current = window.setTimeout(() => {
      setEnteringColumnRowId((current) => (current === next.rowId ? null : current))
      setAutoFocusColumnRowId((current) => (current === next.rowId ? null : current))
      entryAnimationTimeoutRef.current = null
    }, inlineAddColumnResumeDelayMs)
  }

  const updateColumnField = <K extends keyof DraftColumn>(
    rowId: string,
    field: K,
    value: DraftColumn[K],
  ) => {
    setColumns((current) => {
      const next = current.map((column) => {
        if (column.rowId !== rowId) return column
        return { ...column, [field]: value }
      })

      if (field === 'pk') {
        return next.map((column) => {
          if (column.rowId === rowId) {
            if (!value) return { ...column, pk: false }
            return { ...column, pk: true, fk: false, uq: true }
          }
          return value && column.pk ? { ...column, pk: false } : column
        })
      }

      if (field === 'fk') {
        return next.map((column) => {
          if (column.rowId !== rowId) return column
          if (!value) return { ...column, fk: false }
          return { ...column, fk: true, pk: false }
        })
      }

      if (field === 'uq') {
        return next.map((column) => {
          if (column.rowId !== rowId) return column
          if (column.pk) return { ...column, uq: true }
          return { ...column, uq: Boolean(value) }
        })
      }

      return next
    })
  }

  const toggleDescription = (rowId: string) => {
    setColumns((current) =>
      current.map((column) =>
        column.rowId === rowId ? { ...column, descOpen: !column.descOpen } : column,
      ),
    )
  }

  const deleteColumn = (rowId: string) => {
    requestDelete(rowId, () => {
      /*
       * A remoção precisa ficar registrada: no save, "coluna ausente do buffer"
       * é ambíguo entre "removida aqui" e "criada por outro usuário depois que
       * este modal abriu". Só colunas que existiam na semeadura entram — as
       * criadas e apagadas na mesma sessão nunca chegaram ao servidor.
       */
      const target = columns.find((column) => column.rowId === rowId)
      if (target?.id && seededColumnIdsRef.current.has(target.id)) {
        removedColumnIdsRef.current.add(target.id)
      }
      setColumns((current) => current.filter((column) => column.rowId !== rowId))
    })
  }

  const handleDragEnd = (event: DragEndEvent) => {
    setIsSortingColumns(false)
    setHoveredInsertIndex(null)
    const { active, over } = event
    if (!over || active.id === over.id) return

    setColumns((current) => {
      const oldIndex = current.findIndex((column) => column.rowId === active.id)
      const newIndex = current.findIndex((column) => column.rowId === over.id)
      if (oldIndex < 0 || newIndex < 0) return current
      return arrayMove(current, oldIndex, newIndex)
    })
  }

  const handleDragStart = () => {
    setIsSortingColumns(true)
  }

  const handleDragCancel = () => {
    setIsSortingColumns(false)
    setHoveredInsertIndex(null)
  }

  const renderAddColumnButton = (label: string, onClick: () => void) => (
    <button
      type="button"
      onClick={onClick}
      className="w-full rounded-xl border border-dashed px-2 py-1 text-[11px] font-medium"
      style={{
        borderColor: 'rgba(99,102,241,.28)',
        color: 'var(--accent)',
        background: 'rgba(99,102,241,.08)',
      }}
    >
      <div className="flex h-7 w-full items-center justify-center rounded-lg">
        {label}
      </div>
    </button>
  )

  return (
    <>
      <div
        className="fixed inset-0 z-[1200] bg-black/48 p-4"
      >
        <div
          className="mx-auto mt-[40px] flex max-h-[calc(100vh-56px)] w-full max-w-[1280px] flex-col overflow-hidden rounded-2xl border shadow-2xl"
          style={{
            background: 'var(--bg-modal)',
            borderColor: 'var(--border)',
            color: 'var(--text)',
          }}
        >
          <div className="flex items-center justify-between border-b px-5 py-4" style={{ borderColor: 'var(--border)' }}>
            <div>
              <h2 className="text-sm font-semibold">
                {currentTable
                  ? t('tableModal.editTitle', { name: currentTable.fullName })
                  : t('tableModal.newTitle')}
              </h2>
              <p className="mt-1 text-[11px]" style={{ color: 'var(--text-dim)' }}>
                {currentTable ? t('tableModal.editDescription') : t('tableModal.createDescription')}
              </p>
            </div>
            <button
              type="button"
              onClick={requestClose}
              aria-label={t('common.close')}
              title={t('common.close')}
              className="inline-flex h-6 w-6 items-center justify-center text-sm transition-colors"
              style={{ color: 'var(--text-muted)' }}
            >
              <CloseIcon />
            </button>
          </div>

          <div className="grid min-h-0 flex-1 grid-rows-[auto_minmax(360px,1fr)] gap-0 overflow-y-auto xl:grid-cols-[320px_minmax(0,1fr)] xl:grid-rows-1 xl:overflow-hidden">
            <div className="border-b px-5 py-4 xl:border-b-0 xl:border-r" style={{ borderColor: 'var(--border)' }}>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:block xl:space-y-4">
                <div>
                  <label className="mb-1 block text-[10px] uppercase tracking-[0.3px]" style={{ color: 'var(--text-dim)' }}>
                    {t('tableModal.tableName')}
                  </label>
                  <input
                    ref={tableNameInputRef}
                    type="text"
                    value={name}
                    onChange={(event) => setName(sanitizeSqlIdentifierInput(event.target.value))}
                    placeholder={t('tableModal.tableNamePlaceholder')}
                    className="morph-input"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-[10px] uppercase tracking-[0.3px]" style={{ color: 'var(--text-dim)' }}>
                    {t('tableModal.schema')}
                  </label>
                  <input
                    type="text"
                    value={schema}
                    onChange={(event) => setSchema(sanitizeSqlIdentifierInput(event.target.value))}
                    placeholder={t('tableModal.schemaPlaceholder')}
                    className="morph-input"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-[10px] uppercase tracking-[0.3px]" style={{ color: 'var(--text-dim)' }}>
                    {t('tableModal.groupColor')}
                  </label>
                  <MorphSelect
                    ariaLabel={t('tableModal.groupColor')}
                    value={groupId}
                    options={groupOptions}
                    onChange={(nextGroupId) => {
                      setGroupId(nextGroupId ?? '')
                    }}
                    searchable
                    searchPlaceholder={t('tableModal.searchGroupPlaceholder')}
                  />
                </div>

                <div className="sm:col-span-2 lg:col-span-2 xl:col-span-1">
                  <label className="mb-1 block text-[10px] uppercase tracking-[0.3px]" style={{ color: 'var(--text-dim)' }}>
                    {t('tableModal.comment')}
                  </label>
                  <textarea
                    value={comment}
                    onChange={(event) => setComment(event.target.value)}
                    placeholder={t('tableModal.commentPlaceholder')}
                    className="morph-textarea min-h-[64px] xl:min-h-[120px]"
                  />
                </div>

                <div className="rounded-xl border px-3 py-3 text-[10px] xl:col-span-1" style={{ borderColor: 'var(--border)', background: 'var(--bg-card)', color: 'var(--text-muted)' }}>
                  <div className="font-semibold" style={{ color: 'var(--text)' }}>{t('tableModal.fullName')}</div>
                  <div className="mt-1 break-all">{buildFullTableName(schema.trim(), name.trim()) || '—'}</div>
                </div>
              </div>
            </div>

            <div className="flex min-h-[360px] flex-col px-5 py-4 xl:min-h-0">
              <div className="mb-3 flex items-center justify-between gap-3">
                <div>
                  <h3 className="text-xs font-semibold">{t('tableModal.columns')}</h3>
                  <p className="mt-1 text-[10px]" style={{ color: 'var(--text-dim)' }}>
                    {t('tableModal.columnsHelp')}
                  </p>
                </div>
              </div>

              {showRemoteEditNotice ? (
                <div
                  className="mb-3 flex items-center gap-2 rounded-xl border px-3 py-2 text-[11px]"
                  style={{ borderColor: 'rgba(37,99,235,.40)', background: 'rgba(37,99,235,.10)', color: 'var(--text)' }}
                  role="status"
                >
                  <span style={{ color: 'var(--text-dim)' }}>
                    {remoteTableEdit?.actorName
                      ? t('tableModal.remoteEditNotice', { name: remoteTableEdit.actorName })
                      : t('tableModal.remoteEditNoticeAnonymous')}
                  </span>
                </div>
              ) : null}

              {!currentTable && hasDefaultsTemplate ? (
                <div
                  className="mb-3 flex items-center justify-between gap-3 rounded-xl border px-3 py-2 text-[11px]"
                  style={{ borderColor: 'rgba(202,138,4,.40)', background: 'rgba(202,138,4,.10)', color: 'var(--text)' }}
                >
                  <span style={{ color: 'var(--text-dim)' }}>
                    {t('tableModal.defaultsAppliedNotice')}
                  </span>
                  <Button variant="ghost" size="sm" onClick={openDefaults}>
                    {t('tableModal.editDefaults')}
                  </Button>
                </div>
              ) : null}

              {errorMessage ? (
                <div className="mb-3 rounded-xl border px-3 py-2 text-[11px]" style={{ borderColor: 'var(--color-danger-border)', background: 'var(--color-danger-bg)', color: 'var(--color-danger)' }}>
                  {errorMessage}
                </div>
              ) : null}

              <div className="relative min-h-0 flex-1">
                <div
                  className={`pointer-events-none absolute left-1/2 top-[20px] z-10 -translate-x-1/2 transition-all duration-300 ${
                    canScrollColumnsUp ? 'translate-y-0 opacity-100' : '-translate-y-2 opacity-0'
                  }`}
                  style={{ animation: canScrollColumnsUp ? 'morphScrollHint 1.8s ease-in-out infinite' : 'none' }}
                >
                  <div className="rounded-full border px-3 py-1 text-[10px]" style={{ borderColor: 'rgba(99,102,241,.24)', background: 'rgba(15,23,42,.74)', color: '#C7D2FE', backdropFilter: 'blur(8px)' }}>
                    ↑
                  </div>
                </div>

                <div
                  className={`pointer-events-none absolute bottom-2 left-1/2 z-10 -translate-x-1/2 transition-all duration-300 ${
                    canScrollColumnsDown ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0'
                  }`}
                  style={{ animation: canScrollColumnsDown ? 'morphScrollHint 1.8s ease-in-out infinite' : 'none' }}
                >
                  <div className="rounded-full border px-3 py-1 text-[10px]" style={{ borderColor: 'rgba(99,102,241,.24)', background: 'rgba(15,23,42,.74)', color: '#C7D2FE', backdropFilter: 'blur(8px)' }}>
                    ↓
                  </div>
                </div>

                <div ref={columnsScrollRef} className="morph-scrollbar min-h-0 h-full overflow-y-auto pr-1">
                  <div className="sticky top-0 z-10 mb-2" style={{ background: 'var(--bg-modal)' }}>
                    <div className="rounded-xl border border-transparent px-2 py-1">
                      <div className="grid items-center gap-1.5 text-[9px] uppercase tracking-[0.3px] leading-none" style={{ gridTemplateColumns: '20px minmax(0,1fr) minmax(0,188px) 20px 20px 20px 24px 24px 28px 28px', color: 'var(--text-dim)' }}>
                        <span />
                        <span className="block text-left">{t('common.name')}</span>
                        <span className="block text-left">{t('common.type')}</span>
                        <span className="block text-center">PK</span>
                        <span className="block text-center">FK</span>
                        <span className="block text-center">UQ</span>
                        <span className="block text-center" title={t('prompts.notNullTitle')}>NN</span>
                        <span className="block text-center" title={t('prompts.blankTitle')}>BL</span>
                        <span className="flex items-center justify-center" title={t('common.details')} />
                        <span className="flex items-center justify-center" title={t('common.delete')} />
                      </div>
                    </div>
                  </div>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragStart={handleDragStart}
                    onDragCancel={handleDragCancel}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
                      <div className="space-y-1">
                        {columns.map((column, index) => (
                          <div key={column.rowId} data-column-row-id={column.rowId}>
                            <div
                              className="overflow-hidden transition-all duration-150"
                              onMouseEnter={() => {
                                if (!isSortingColumns) setHoveredInsertIndex(index)
                              }}
                              onMouseLeave={() => {
                                setHoveredInsertIndex((current) => (current === index ? null : current))
                              }}
                              style={{
                                height:
                                  !isSortingColumns && hoveredInsertIndex === index && !enteringColumnRowId
                                    ? '2.45rem'
                                    : '0.3rem',
                                marginBottom:
                                  !isSortingColumns && hoveredInsertIndex === index && !enteringColumnRowId
                                    ? '0.175rem'
                                    : '0',
                              }}
                            >
                              {!isSortingColumns && hoveredInsertIndex === index && !enteringColumnRowId ? (
                                renderAddColumnButton(t('tableModal.addColumnHere'), () => addColumn(index))
                              ) : null}
                            </div>
                            <ColumnRow
                              column={column}
                              impact={impactByRowId[column.rowId] ?? { hasForeignKeyRelation: false, hasIndexImpact: false }}
                              isDeleteConfirming={isConfirming(column.rowId)}
                              isEntering={enteringColumnRowId === column.rowId}
                              autoFocusName={autoFocusColumnRowId === column.rowId}
                              onDeleteClick={deleteColumn}
                              onToggleDescription={toggleDescription}
                              onChangeField={updateColumnField}
                              dialect={dialect}
                            />
                          </div>
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>

                  {!columns.length ? (
                    <div className="rounded-xl border border-dashed px-4 py-8 text-center text-[11px]" style={{ borderColor: 'var(--border)', color: 'var(--text-dim)' }}>
                      {t('tableModal.emptyColumns')}
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="mt-2 px-2 pr-3">
                {renderAddColumnButton(t('tableModal.addColumn'), () => addColumn())}
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 border-t px-5 py-4" style={{ borderColor: 'var(--border)' }}>
            <Button variant="ghost" onClick={requestClose}>
              {t('common.cancel')}
            </Button>
            <Button variant="primary" onClick={handleSave}>
              {t('common.save')}
            </Button>
          </div>
        </div>
      </div>

      <Dialog
        open={showDiscardDialog}
        title={t('tableModal.unsavedTitle')}
        onClose={() => setShowDiscardDialog(false)}
        onConfirm={() => {
          setShowDiscardDialog(false)
          closeModal()
        }}
        footer={(
          <>
            <Button variant="ghost" onClick={() => setShowDiscardDialog(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                setShowDiscardDialog(false)
                closeModal()
              }}
            >
              {t('tableModal.discardChanges')}
            </Button>
          </>
        )}
      >
        {t('tableModal.unsavedMessage')}
      </Dialog>

      <style>{`
        @keyframes morphColumnRowIn {
          0% {
            opacity: 0;
            transform: translateY(18px) scale(.985);
          }
          100% {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @keyframes morphScrollHint {
          0%, 100% { transform: translateX(-50%) translateY(0); }
          50% { transform: translateX(-50%) translateY(-3px); }
        }
      `}</style>
    </>
  )
}
